// ============================================================
// Permissions Siverix (bitmask BigInt 64-bit)
// Source de vérité — doit rester en phase avec apps/api/src/services/permissions.ts
// ============================================================
export const Permission = {
    // ---- Vue & lecture ----
    VIEW_CHANNEL: 1n << 0n,
    READ_MESSAGE_HISTORY: 1n << 1n,
    // ---- Messages ----
    SEND_MESSAGES: 1n << 2n,
    EMBED_LINKS: 1n << 3n,
    ATTACH_FILES: 1n << 4n,
    ADD_REACTIONS: 1n << 5n,
    USE_EXTERNAL_EMOJIS: 1n << 6n,
    MENTION_EVERYONE: 1n << 7n,
    MANAGE_MESSAGES: 1n << 8n, // edit/delete d'autrui
    // ---- Voice ----
    VOICE_CONNECT: 1n << 10n,
    VOICE_SPEAK: 1n << 11n,
    VOICE_VIDEO: 1n << 12n,
    VOICE_SCREEN_SHARE: 1n << 13n,
    VOICE_MUTE_MEMBERS: 1n << 14n,
    VOICE_MOVE_MEMBERS: 1n << 15n,
    // ---- Channels ----
    MANAGE_CHANNELS: 1n << 20n,
    CREATE_INVITE: 1n << 21n,
    // ---- Membres ----
    KICK_MEMBERS: 1n << 22n,
    BAN_MEMBERS: 1n << 23n,
    MANAGE_NICKNAMES: 1n << 24n,
    // ---- Rôles ----
    MANAGE_ROLES: 1n << 25n,
    // ---- Community ----
    MANAGE_COMMUNITY: 1n << 26n,
    VIEW_AUDIT_LOG: 1n << 27n,
    // [2.3] Événements du serveur (raids, donjons, sorties) : créer, modifier et
    // annuler ceux des autres. Le créateur garde la main sur les siens.
    MANAGE_EVENTS: 1n << 28n,
    // [2.12] Tous les droits du serveur, et passe outre les surcharges de salon
    // (comme Discord). Ne dispense PAS de la hiérarchie des rôles.
    ADMINISTRATOR: 1n << 29n,
    // ---- Instance-level ----
    // Porté seulement par l'administrateur de l'instance (user.isAdmin), jamais
    // par un rôle de serveur depuis la 2.12 (masque ROLE_PERMISSIONS_MASK).
    INSTANCE_ADMIN: 1n << 30n,
};
/**
 * Combine plusieurs permissions en un bitmask.
 */
export function combinePermissions(...flags) {
    let result = 0n;
    for (const flag of flags) {
        result |= Permission[flag];
    }
    return result;
}
/**
 * Vérifie si un bitmask contient une permission donnée.
 */
export function hasPermission(bitmask, flag) {
    return (bitmask & Permission[flag]) !== 0n;
}
/**
 * Vérifie si un bitmask contient TOUTES les permissions données.
 */
export function hasAllPermissions(bitmask, ...flags) {
    return flags.every((flag) => hasPermission(bitmask, flag));
}
/**
 * Vérifie si un bitmask contient AU MOINS UNE des permissions données.
 */
export function hasAnyPermission(bitmask, ...flags) {
    return flags.some((flag) => hasPermission(bitmask, flag));
}
/**
 * Serialise un bitmask en string (pour JSON/DB — BigInt n'est pas JSON-serializable).
 */
export function serializePermissions(bitmask) {
    return bitmask.toString();
}
/**
 * Deserialise une string en bitmask BigInt.
 */
export function deserializePermissions(value) {
    return BigInt(value);
}
// ---------- Presets utiles ----------
/** Permissions du rôle @everyone par défaut (texte basique + voix, caméra et partage d'écran). */
export const DEFAULT_EVERYONE_PERMISSIONS = combinePermissions('VIEW_CHANNEL', 'READ_MESSAGE_HISTORY', 'SEND_MESSAGES', 'EMBED_LINKS', 'ATTACH_FILES', 'ADD_REACTIONS', 'USE_EXTERNAL_EMOJIS', 'VOICE_CONNECT', 'VOICE_SPEAK', 'VOICE_VIDEO', 'VOICE_SCREEN_SHARE');
/** Permissions de l'owner (tout). */
export const OWNER_PERMISSIONS = ~0n; // all bits set
/** Permissions du rôle Admin instance. */
export const INSTANCE_ADMIN_PERMISSIONS = combinePermissions('INSTANCE_ADMIN', 'VIEW_AUDIT_LOG');
// ---------- [2.12] Masques, lecture tolérante, calcul des droits ----------
/** Toutes les permissions, dans l'ordre de déclaration. */
export const ALL_PERMISSION_FLAGS = Object.keys(Permission);
/** Toutes les permissions d'un serveur : tout sauf INSTANCE_ADMIN (= 1072758271). */
export const ALL_COMMUNITY_PERMISSIONS = ALL_PERMISSION_FLAGS.reduce((acc, flag) => (flag === 'INSTANCE_ADMIN' ? acc : acc | Permission[flag]), 0n);
/**
 * Masque appliqué EN SILENCE à toute permission de rôle reçue (routes de
 * serveur et routes /admin) : le bit 30 et tout bit inconnu disparaissent.
 */
export const ROLE_PERMISSIONS_MASK = ALL_COMMUNITY_PERMISSIONS;
/**
 * Permissions qu'une surcharge de salon peut porter : seulement celles que
 * l'API calcule PAR SALON. Les autres (gestion du serveur, ADMINISTRATOR…)
 * n'auraient aucun effet dans un salon.
 */
export const CHANNEL_OVERRIDE_FLAGS = [
    'VIEW_CHANNEL',
    'READ_MESSAGE_HISTORY',
    'SEND_MESSAGES',
    'ATTACH_FILES',
    'ADD_REACTIONS',
    'MENTION_EVERYONE',
    'MANAGE_MESSAGES',
    'VOICE_CONNECT',
    'VOICE_SPEAK',
    'VOICE_VIDEO',
    'VOICE_SCREEN_SHARE',
    'VOICE_MUTE_MEMBERS',
    'VOICE_MOVE_MEMBERS',
];
export const CHANNEL_OVERRIDE_MASK = combinePermissions(...CHANNEL_OVERRIDE_FLAGS);
/**
 * Permissions jamais présentées dans un éditeur : l'API ne les applique pas
 * (encore), ou elles ne concernent pas un serveur. Un éditeur conserve leur
 * valeur actuelle quand il enregistre.
 */
export const HIDDEN_PERMISSION_FLAGS = [
    'EMBED_LINKS',
    'USE_EXTERNAL_EMOJIS',
    'BAN_MEMBERS',
    'MANAGE_NICKNAMES',
    'VIEW_AUDIT_LOG',
    'INSTANCE_ADMIN',
];
/**
 * Lecture tolérante d'un bitmask sérialisé : vide ou illisible -> 0n. « -1 »
 * (propriétaire, tous les droits) est lu tel quel.
 */
export function parsePermissions(value) {
    if (typeof value !== 'string' || !/^-?\d{1,20}$/.test(value.trim()))
        return 0n;
    try {
        return BigInt(value.trim());
    }
    catch {
        return 0n;
    }
}
/**
 * Vrai si ces droits passent outre tout le reste : propriétaire (tous les
 * bits), ADMINISTRATOR, ou administration de l'instance.
 */
export function hasAllPermissionsBypass(bits) {
    return (bits & (Permission.ADMINISTRATOR | Permission.INSTANCE_ADMIN)) !== 0n;
}
/** Carte `can` des réponses « mes droits » : une entrée par permission. */
export function permissionsToCan(bits) {
    const can = {};
    for (const flag of ALL_PERMISSION_FLAGS)
        can[flag] = (bits & Permission[flag]) !== 0n;
    return can;
}
/**
 * Droits d'un utilisateur au niveau du serveur (sans surcharge de salon).
 * Propriétaire et administrateur de l'instance : tout. Non-membre : rien.
 * Sinon @everyone | rôles ; ADMINISTRATOR donne toutes les permissions du
 * serveur.
 */
export function computeServerPermissions(input) {
    if (input.isOwner || input.isInstanceAdmin)
        return OWNER_PERMISSIONS;
    if (!input.isMember)
        return 0n;
    let p = input.everyone;
    for (const r of input.roles)
        p |= r;
    p &= ROLE_PERMISSIONS_MASK;
    if ((p & Permission.ADMINISTRATOR) !== 0n)
        return ALL_COMMUNITY_PERMISSIONS;
    return p;
}
/**
 * Droits dans un salon (ordre Discord) : propriétaire, administrateur de
 * l'instance et ADMINISTRATOR passent outre toute surcharge ; sinon surcharge
 * @everyone, puis rôles du membre (∪refus puis ∪autorisations), puis membre.
 * Sans VIEW_CHANNEL au bout du compte : aucun droit dans ce salon.
 */
export function computeChannelPermissions(input) {
    const { server } = input;
    if (hasAllPermissionsBypass(server))
        return server;
    if (server === 0n)
        return 0n;
    let p = server;
    const mask = CHANNEL_OVERRIDE_MASK;
    const ev = input.everyoneRoleId
        ? input.overrides.find((o) => o.roleId === input.everyoneRoleId)
        : undefined;
    if (ev) {
        p &= ~(ev.deny & mask);
        p |= ev.allow & mask;
    }
    const mine = new Set(input.memberRoleIds);
    let allow = 0n;
    let deny = 0n;
    for (const o of input.overrides) {
        if (o.roleId && o.roleId !== input.everyoneRoleId && mine.has(o.roleId)) {
            allow |= o.allow & mask;
            deny |= o.deny & mask;
        }
    }
    p &= ~deny;
    p |= allow;
    const own = input.memberId
        ? input.overrides.find((o) => o.memberId === input.memberId)
        : undefined;
    if (own) {
        p &= ~(own.deny & mask);
        p |= own.allow & mask;
    }
    if ((p & Permission.VIEW_CHANNEL) === 0n)
        return 0n;
    return p;
}
